import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

/**
 * Zone Editor Controller
 *
 * Handles dynamic interactions for the ROI zone management interface:
 * - Adding application types to zones
 * - Toggling zone sections (expand/collapse)
 * - Confirming destructive actions
 *
 * @example
 * <div data-controller="zone-editor"
 *      data-zone-editor-zone-applications-url-value="/admin/roi/calculations/123/zones/456/zone_applications">
 *
 *   <select data-zone-editor-target="applicationTypeSelect">...</select>
 *   <button data-action="click->zone-editor#addApplicationType">Add</button>
 *
 *   <div data-zone-editor-target="section" data-section-id="risk-areas">
 *     <button data-action="click->zone-editor#toggleSection">Toggle</button>
 *     <div data-zone-editor-target="sectionContent">...</div>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    "applicationTypeSelect",
    "addButton",
    "section",
    "sectionContent",
    "loadingIndicator",
  ]

  static values = {
    zoneApplicationsUrl: String,
    expanded: { type: Boolean, default: true },
  }

  static classes = ["hidden", "loading"]

  connect() {
    // Initialize section states from localStorage
    this.restoreSectionStates()
  }

  /**
   * Add an application type to the current zone
   * Submits via AJAX and reloads the page on success
   */
  async addApplicationType(event) {
    event.preventDefault()

    if (!this.hasApplicationTypeSelectTarget) {
      console.warn("No application type select target found")
      return
    }

    const applicationTypeId = this.applicationTypeSelectTarget.value
    if (!applicationTypeId) {
      this.showToast("Please select an application type", "warning")
      return
    }

    this.setLoading(true)

    try {
      const response = await post(this.zoneApplicationsUrlValue, {
        body: {
          roi_zone_application: {
            application_type_id: applicationTypeId,
          },
        },
        contentType: "application/json",
        responseKind: "json",
        credentials: "include",
      })

      if (response.ok) {
        this.showToast("Application type added successfully", "success")
        // Reload to show updated zone applications
        window.location.reload()
      } else {
        const data = await response.json
        this.showToast(data.error || "Failed to add application type", "error")
      }
    } catch (error) {
      console.error("Error adding application type:", error)
      this.showToast("An error occurred. Please try again.", "error")
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Confirm before removing a zone application
   * Uses a custom confirmation dialog dispatched via events
   */
  confirmRemoveApplication(event) {
    const applicationName =
      event.currentTarget.dataset.applicationName || "this application"
    const link = event.currentTarget

    // Always prevent default - we'll handle submission manually if confirmed
    event.preventDefault()

    // Dispatch confirmation request event
    const confirmed = new Promise((resolve) => {
      const handleConfirm = (e) => {
        window.removeEventListener("confirm:response", handleConfirm)
        resolve(e.detail.confirmed)
      }
      window.addEventListener("confirm:response", handleConfirm)

      window.dispatchEvent(
        new CustomEvent("confirm:show", {
          detail: {
            title: "Remove Application Type",
            message: `Are you sure you want to remove ${applicationName} from this zone? This will also remove all associated risk areas.`,
            confirmText: "Remove",
            cancelText: "Cancel",
            variant: "danger",
          },
        }),
      )

      // Fallback to native confirm if no listener responds within 100ms
      setTimeout(() => {
        window.removeEventListener("confirm:response", handleConfirm)
        // Use native confirm as fallback
        const nativeConfirm = window.confirm(
          `Are you sure you want to remove ${applicationName} from this zone?`,
        )
        resolve(nativeConfirm)
      }, 100)
    })

    confirmed.then((result) => {
      if (result && link.href) {
        // For turbo links, use Turbo.visit with method
        const method = link.dataset.turboMethod || "delete"
        if (method === "delete") {
          // Submit as a form for DELETE requests
          const form = document.createElement("form")
          form.method = "POST"
          form.action = link.href
          form.style.display = "none"

          const methodInput = document.createElement("input")
          methodInput.type = "hidden"
          methodInput.name = "_method"
          methodInput.value = "delete"
          form.appendChild(methodInput)

          const csrfToken = document.querySelector(
            'meta[name="csrf-token"]',
          )?.content
          if (csrfToken) {
            const tokenInput = document.createElement("input")
            tokenInput.type = "hidden"
            tokenInput.name = "authenticity_token"
            tokenInput.value = csrfToken
            form.appendChild(tokenInput)
          }

          document.body.appendChild(form)
          form.submit()
        } else {
          window.location.href = link.href
        }
      }
    })
  }

  /**
   * Toggle a collapsible section
   */
  toggleSection(event) {
    const section = event.currentTarget.closest(
      "[data-zone-editor-target='section']",
    )
    if (!section) return

    const content = section.querySelector(
      "[data-zone-editor-target='sectionContent']",
    )
    const icon = event.currentTarget.querySelector("[data-toggle-icon]")

    if (content) {
      const isHidden = content.classList.contains("hidden")

      if (isHidden) {
        content.classList.remove("hidden")
        if (icon) icon.style.transform = "rotate(0deg)"
      } else {
        content.classList.add("hidden")
        if (icon) icon.style.transform = "rotate(-90deg)"
      }

      // Save state to localStorage
      const sectionId = section.dataset.sectionId
      if (sectionId) {
        this.saveSectionState(sectionId, isHidden)
      }
    }
  }

  /**
   * Expand all sections
   */
  expandAll() {
    this.sectionContentTargets.forEach((content) => {
      content.classList.remove("hidden")
    })
    this.saveAllSectionStates(true)
  }

  /**
   * Collapse all sections
   */
  collapseAll() {
    this.sectionContentTargets.forEach((content) => {
      content.classList.add("hidden")
    })
    this.saveAllSectionStates(false)
  }

  // Private methods

  setLoading(loading) {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = loading
      if (loading) {
        this.addButtonTarget.classList.add(
          ...(this.loadingClasses || ["opacity-50", "cursor-wait"]),
        )
      } else {
        this.addButtonTarget.classList.remove(
          ...(this.loadingClasses || ["opacity-50", "cursor-wait"]),
        )
      }
    }

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !loading)
    }
  }

  showToast(message, type = "info") {
    // Dispatch custom event for toast notification system
    window.dispatchEvent(
      new CustomEvent("toast:show", {
        detail: { message, type, duration: 4000 },
      }),
    )
  }

  saveSectionState(sectionId, expanded) {
    try {
      const key = `zone-editor-section-${sectionId}`
      localStorage.setItem(key, expanded ? "expanded" : "collapsed")
    } catch {
      // localStorage may not be available
    }
  }

  saveAllSectionStates(expanded) {
    this.sectionTargets.forEach((section) => {
      const sectionId = section.dataset.sectionId
      if (sectionId) {
        this.saveSectionState(sectionId, expanded)
      }
    })
  }

  restoreSectionStates() {
    this.sectionTargets.forEach((section) => {
      const sectionId = section.dataset.sectionId
      if (!sectionId) return

      try {
        const key = `zone-editor-section-${sectionId}`
        const state = localStorage.getItem(key)
        const content = section.querySelector(
          "[data-zone-editor-target='sectionContent']",
        )

        if (content && state === "collapsed") {
          content.classList.add("hidden")
          const icon = section.querySelector("[data-toggle-icon]")
          if (icon) icon.style.transform = "rotate(-90deg)"
        }
      } catch {
        // localStorage may not be available
      }
    })
  }
}
